import type { AdType, CreativeType, DisplayEnum } from '../../../../../configs';
import { convertAdType, convertCreative, isRecognizedConfig } from '../../../../../configs';
import { getPermittedAdTypes } from '../../../../../configs/permittedAdTypes';
import { getPermittedCreativeTypes } from '../../../../../configs/permittedCreativeTypes';
import type { TagSheetAd, TagSheetLineItem } from '../types';
import { getResolvableCreativeTypes } from './creative';

export type AdLineItemResolution = {
  adType: AdType;
  creativeType: CreativeType;
};

export const getPermittedAdTypesForLineItem = (lineItem: TagSheetLineItem): AdType[] => {
  if (lineItem.adProduct && isRecognizedConfig(lineItem.adProduct)) {
    const permitted = getPermittedAdTypes({
      adProductKey: lineItem.adProduct.key,
      displayPublisherTarget: lineItem.displayPublisherTarget ?? null,
    });

    return permitted.map((config) => config.key);
  }

  return [];
};

export const getCreativeTypesForAdType = (adType: AdType): Set<CreativeType> => {
  const permitted = getPermittedCreativeTypes(adType);

  return new Set(Object.values(permitted).map((config) => config.key));
};

export const findCompatibleAdType = (
  creativeType: DisplayEnum<CreativeType>,
  permittedAdTypes: AdType[]
): AdType | null => {
  for (const adType of permittedAdTypes) {
    const supportedCreativeTypes = getCreativeTypesForAdType(adType);

    if (supportedCreativeTypes.has(creativeType.key as CreativeType)) return adType;
  }

  return null;
};

export const resolveAdForLineItem = (
  creativeType: CreativeType,
  lineItem: TagSheetLineItem
): AdLineItemResolution | null => {
  const permittedAdTypes = getPermittedAdTypesForLineItem(lineItem);

  for (const candidateCreativeType of getResolvableCreativeTypes(creativeType)) {
    const compatibleAdType = findCompatibleAdType(convertCreative(candidateCreativeType), permittedAdTypes);

    if (compatibleAdType) {
      return { adType: compatibleAdType, creativeType: candidateCreativeType };
    }
  }

  return null;
};

export const transformAdForLineItem = (ad: TagSheetAd, targetLineItem: TagSheetLineItem): TagSheetAd => {
  const resolution = resolveAdForLineItem(ad.creative.creativeType.key as CreativeType, targetLineItem);

  if (!resolution) {
    return ad;
  }

  let transformedAd = ad;

  if (resolution.adType !== ad.adType.key) {
    transformedAd = { ...transformedAd, adType: convertAdType(resolution.adType) };
  }

  if (resolution.creativeType !== ad.creative.creativeType.key) {
    transformedAd = {
      ...transformedAd,
      creative: {
        ...transformedAd.creative,
        creativeType: convertCreative(resolution.creativeType),
      },
    };
  }

  return transformedAd;
};
